import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectAuthenticatedUser } from 'src/store/selectors/auth';
import {
  useServiceRequestAPI,
  UseServiceRequestAPIReturn,
} from 'src/pages/ServiceRequest/CustomHooks/useServiceRequestAPI';
import { IServiceRequest } from 'src/services/api/ServiceRequestDashboard/apiContracts';
import { CsvDateFormat } from 'src/services/client-types';
import {
  useDownloadCSVData,
  UseDownloadCSVDataReturn,
} from 'src/pages/SharedPageCustomHooks/UseDownloadCSV/useDownloadCSVData';
import {
  useDownloadPDF,
  UseDownloadPDFReturn,
} from 'src/pages/SharedPageCustomHooks/UseDownloadPDF/useDownloadPDF';
import {
  useServiceRequestDashboardApi,
  UseServiceRequestDashboardApiReturn,
} from 'src/services/api/ServiceRequestDashboard/serviceRequestDashboard';
import {
  useScopedServiceRequestsFormState,
  UseScopedServiceRequestsFormStateReturn,
} from 'src/pages/ServiceRequests/CustomHooks/useScopedServiceRequestsFormState';
import { useBulkStatusUpdate } from 'src/pages/ServiceRequests/CustomHooks/useBulkStatusUpdate';

export interface UseSuiteServiceRequestsOrchestratorArgs {
  suiteId: string;
  navigateTo: string;
  navLabel: string;
}

export interface UseSuiteServiceRequestsOrchestratorReturn {
  serviceRequests: IServiceRequest[];
  visibleServiceRequests: IServiceRequest[];
  isLoading: boolean;
  refetch: () => Promise<void>;

  searchQuery: string;
  setSearchQuery: (query: string) => void;

  showResolvedOnly: boolean;
  toggleShowResolved: () => void;

  selectedIds: string[];
  allVisibleSelected: boolean;
  idsForCsvExport: string[];
  toggleSelection: (id: string) => void;
  toggleSelectionBatch: (ids: string[]) => void;
  toggleSelectAllVisible: () => void;
  clearSelection: () => void;

  selectedServiceRequests: IServiceRequest[];
  bulkUpdateStatus: (status: string) => Promise<void>;
  isAssignOverlayOpen: boolean;
  closeAssignOverlay: () => void;
  isDeclineOverlayOpen: boolean;
  closeDeclineOverlay: () => void;

  expandedGroupIds: string[];
  toggleGroupExpansion: (id: string) => void;

  isDownloadOverlayOpen: boolean;
  openDownloadOverlay: () => void;
  closeDownloadOverlay: () => void;

  onDownloadCSV: (dateFormat: CsvDateFormat) => Promise<boolean>;
  onDownloadPDF: () => Promise<boolean>;
  isCSVLoading: boolean;
  isPDFLoading: boolean;

  navigateTo: string;
  navLabel: string;

  showColumnSelectionDialog: boolean;
  openColumnSelectionDialog: () => void;
  closeColumnSelectionDialog: () => void;

  reset: () => void;
}

export const useSuiteServiceRequestsOrchestrator = ({
  suiteId,
  navigateTo,
  navLabel,
}: UseSuiteServiceRequestsOrchestratorArgs): UseSuiteServiceRequestsOrchestratorReturn => {
  const user: any = useSelector(selectAuthenticatedUser);
  const userId: string = user?.id ?? '';

  const srDashboardApi: UseServiceRequestDashboardApiReturn =
    useServiceRequestDashboardApi();

  const serviceRequests: IServiceRequest[] = srDashboardApi.serviceRequests;
  const isLoading: boolean = srDashboardApi.listLoading;

  const formState: UseScopedServiceRequestsFormStateReturn =
    useScopedServiceRequestsFormState({ serviceRequests });

  const serviceRequestApi: UseServiceRequestAPIReturn = useServiceRequestAPI();

  const [isAssignOverlayOpen, setIsAssignOverlayOpen] =
    useState<boolean>(false);
  const [isDeclineOverlayOpen, setIsDeclineOverlayOpen] =
    useState<boolean>(false);

  const selectedServiceRequests: IServiceRequest[] = useMemo(() => {
    const selectedIdSet: Set<string> = new Set(formState.selectedIds);
    return serviceRequests.filter((sr: IServiceRequest) =>
      selectedIdSet.has(sr.id)
    );
  }, [serviceRequests, formState.selectedIds]);

  const csvDownload: UseDownloadCSVDataReturn = useDownloadCSVData();
  const pdfDownload: UseDownloadPDFReturn = useDownloadPDF();

  const onDownloadCSV = async (dateFormat: CsvDateFormat): Promise<boolean> => {
    return await csvDownload.onDownloadCSV(formState.idsForCsvExport, dateFormat);
  };

  const onDownloadPDF = async (): Promise<boolean> => {
    return await pdfDownload.onDownloadPDF(
      userId,
      formState.idsForCsvExport.join('&')
    );
  };

  const refetch = async (): Promise<void> => {
    formState.clearSelection();
    await srDashboardApi.fetchBySuite(suiteId);
  };

  const closeAssignOverlay = (): void => {
    setIsAssignOverlayOpen(false);
  };

  const closeDeclineOverlay = (): void => {
    setIsDeclineOverlayOpen(false);
  };

  const { bulkUpdateStatus } = useBulkStatusUpdate({
    selectedIds: formState.selectedIds,
    buildStatusPayload: (status: string) => ({
      serviceRequest: formState.selectedIds.map((id: string) => ({ id })),
      name: status,
      userId,
      propertyId: selectedServiceRequests[0]?.propertyId ?? '',
    }),
    onAssignRequested: () => setIsAssignOverlayOpen(true),
    onDeclineRequested: () => setIsDeclineOverlayOpen(true),
    updateStatus: serviceRequestApi.bulkUpdateStatus,
    refetch,
  });

  useEffect(() => {
    if (suiteId) {
      void refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suiteId]);

  const reset = (): void => {
    formState.reset();
    setIsAssignOverlayOpen(false);
    setIsDeclineOverlayOpen(false);
  };

  return {
    serviceRequests,
    visibleServiceRequests: formState.visibleServiceRequests,
    isLoading,
    refetch,

    searchQuery: formState.searchQuery,
    setSearchQuery: formState.setSearchQuery,

    showResolvedOnly: formState.showResolvedOnly,
    toggleShowResolved: formState.toggleShowResolved,

    selectedIds: formState.selectedIds,
    allVisibleSelected: formState.allVisibleSelected,
    idsForCsvExport: formState.idsForCsvExport,
    toggleSelection: formState.toggleSelection,
    toggleSelectionBatch: formState.toggleSelectionBatch,
    toggleSelectAllVisible: formState.toggleSelectAllVisible,
    clearSelection: formState.clearSelection,

    selectedServiceRequests,
    bulkUpdateStatus,
    isAssignOverlayOpen,
    closeAssignOverlay,
    isDeclineOverlayOpen,
    closeDeclineOverlay,

    expandedGroupIds: formState.expandedGroupIds,
    toggleGroupExpansion: formState.toggleGroupExpansion,

    isDownloadOverlayOpen: formState.isDownloadOverlayOpen,
    openDownloadOverlay: formState.openDownloadOverlay,
    closeDownloadOverlay: formState.closeDownloadOverlay,

    onDownloadCSV,
    onDownloadPDF,
    isCSVLoading: csvDownload.isLoading,
    isPDFLoading: pdfDownload.isLoading,

    navigateTo,
    navLabel,

    showColumnSelectionDialog: formState.showColumnSelectionDialog,
    openColumnSelectionDialog: formState.openColumnSelectionDialog,
    closeColumnSelectionDialog: formState.closeColumnSelectionDialog,

    reset,
  };
};
